// Beginner-friendly plugin name and description mappings
// Maps technical plugin slugs to plain-English names and descriptions

export type BeginnerCategory = {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
};

export type BeginnerPluginInfo = {
  slug: string;
  friendlyName: string;
  friendlyDescription: string;
  category: string;
};

export const BEGINNER_CATEGORIES: BeginnerCategory[] = [
  {
    id: "Image Creation",
    name: "Image Creation",
    description: "Generate, edit, and enhance images with AI",
    icon: "image",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
  },
  {
    id: "SEO",
    name: "SEO",
    description: "Optimize your website to rank higher on Google",
    icon: "search",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  {
    id: "Social Media",
    name: "Social Media",
    description: "Create posts, manage accounts, and grow your audience",
    icon: "share",
    color: "text-pink-600",
    bgColor: "bg-pink-50",
    borderColor: "border-pink-200",
  },
  {
    id: "Email Marketing",
    name: "Email Marketing",
    description: "Write email sequences that convert",
    icon: "mail",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
  },
  {
    id: "Content Writing",
    name: "Content Writing",
    description: "Write blog posts, ad copy, and marketing content",
    icon: "pencil",
    color: "text-green-600",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
  },
  {
    id: "Video",
    name: "Video",
    description: "Create AI-powered videos for any platform",
    icon: "video",
    color: "text-red-600",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
];

export const BEGINNER_PLUGINS: BeginnerPluginInfo[] = [
  // Image Creation
  {
    slug: "nano-banana-pro",
    friendlyName: "AI Image Creator",
    friendlyDescription:
      "Generate stunning images from text descriptions. Just describe what you want and get a professional image in seconds.",
    category: "Image Creation",
  },
  {
    slug: "background-removal",
    friendlyName: "Background Remover",
    friendlyDescription:
      "Instantly remove backgrounds from any photo. Perfect for product images, profile pictures, and social media posts.",
    category: "Image Creation",
  },
  {
    slug: "image-upscaling",
    friendlyName: "Image Enhancer",
    friendlyDescription:
      "Make blurry or small images sharp and high-resolution. Upscale photos without losing quality.",
    category: "Image Creation",
  },
  {
    slug: "ai-product-photography",
    friendlyName: "Product Photo Creator",
    friendlyDescription:
      "Generate professional product photography with AI. Get studio-quality shots without a camera.",
    category: "Image Creation",
  },
  // SEO
  {
    slug: "seo-audit",
    friendlyName: "SEO Auditor",
    friendlyDescription:
      "Scan your website and get a clear report on what to fix to rank higher on Google. Plain-English recommendations.",
    category: "SEO",
  },
  {
    slug: "seo-geo-claude-skills",
    friendlyName: "SEO Toolkit",
    friendlyDescription:
      "20 specialized tools for keyword research, content optimization, schema markup, and technical SEO.",
    category: "SEO",
  },
  // Social Media
  {
    slug: "social-media-manager",
    friendlyName: "Social Media Manager",
    friendlyDescription:
      "Plan your social media strategy, create content calendars, and draft posts for all your platforms.",
    category: "Social Media",
  },
  {
    slug: "social-content",
    friendlyName: "Social Content Creator",
    friendlyDescription:
      "Write engaging social media posts, captions, and hashtags optimized for each platform.",
    category: "Social Media",
  },
  {
    slug: "twitter-automation",
    friendlyName: "Twitter/X Manager",
    friendlyDescription:
      "Automate your Twitter/X presence — schedule posts, engage with followers, and grow your audience.",
    category: "Social Media",
  },
  // Email Marketing
  {
    slug: "email-sequence",
    friendlyName: "Email Sequence Builder",
    friendlyDescription:
      "Create automated email sequences that nurture leads and drive sales. Welcome series, follow-ups, and more.",
    category: "Email Marketing",
  },
  {
    slug: "email-best-practices",
    friendlyName: "Email Deliverability Guide",
    friendlyDescription:
      "Stop your emails from going to spam. Get actionable tips on subject lines, formatting, and sending practices.",
    category: "Email Marketing",
  },
  // Content Writing
  {
    slug: "content-strategy-suite",
    friendlyName: "Content Strategist",
    friendlyDescription:
      "Plan your content calendar, generate blog post ideas, and create SEO-optimized articles and newsletters.",
    category: "Content Writing",
  },
  {
    slug: "ad-copy-generator",
    friendlyName: "Ad Copy Writer",
    friendlyDescription:
      "Write high-converting ad copy for Google Ads, Facebook Ads, and sales pages using proven copywriting frameworks.",
    category: "Content Writing",
  },
  {
    slug: "humanizer",
    friendlyName: "Writing Polisher",
    friendlyDescription:
      "Make AI-generated text sound natural and human. Remove robotic phrasing and add personality to your writing.",
    category: "Content Writing",
  },
  // Video
  {
    slug: "ai-video-generation",
    friendlyName: "AI Video Creator",
    friendlyDescription:
      "Generate videos from text descriptions using 40+ AI models including Google Veo and more.",
    category: "Video",
  },
  {
    slug: "ai-marketing-videos",
    friendlyName: "Marketing Video Maker",
    friendlyDescription:
      "Create professional marketing videos for ads, product launches, and brand promotions.",
    category: "Video",
  },
  {
    slug: "ai-avatar-video",
    friendlyName: "Avatar Video Creator",
    friendlyDescription:
      "Create talking head videos with AI avatars. Perfect for tutorials, presentations, and social media.",
    category: "Video",
  },
  {
    slug: "ai-social-media-content",
    friendlyName: "Social Video Creator",
    friendlyDescription:
      "Create short-form videos optimized for TikTok, Instagram Reels, YouTube Shorts, and Twitter/X.",
    category: "Video",
  },
];

// Lookup helpers
export function getBeginnerPlugin(slug: string): BeginnerPluginInfo | undefined {
  return BEGINNER_PLUGINS.find((p) => p.slug === slug);
}

export function getBeginnerCategory(id: string): BeginnerCategory | undefined {
  return BEGINNER_CATEGORIES.find((c) => c.id === id);
}

export function getPluginsByCategory(categoryId: string): BeginnerPluginInfo[] {
  return BEGINNER_PLUGINS.filter((p) => p.category === categoryId);
}

export const EASY_START_SLUG = "easy-start";
